import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, Trophy, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ReviewCard from '../components/common/ReviewCard';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

const ReviewPage = () => {
  const navigate = useNavigate();
  const { reviewQueue, updateReviewStrength } = useApp();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState({ correct: 0, hard: 0, wrong: 0 });
  const [isComplete, setIsComplete] = useState(false);

  const currentPhrase = reviewQueue[currentIndex];
  const progress = ((currentIndex + 1) / reviewQueue.length) * 100;

  const handleResult = (result) => {
    // Update results
    setResults(prev => ({
      ...prev,
      [result]: prev[result] + 1
    }));

    // Update phrase strength based on result
    let newStrength = currentPhrase.strength;
    if (result === 'correct') {
      newStrength = Math.min(1, newStrength + 0.2);
    } else if (result === 'hard') {
      newStrength = Math.min(1, newStrength + 0.05);
    } else {
      newStrength = Math.max(0, newStrength - 0.2);
    }
    updateReviewStrength(currentPhrase.id, newStrength);

    // Move to next or complete
    if (currentIndex < reviewQueue.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  // Empty state
  if (reviewQueue.length === 0) {
    return (
      <Layout title="Revisão" showBack>
        <div className="px-4 py-12 text-center space-y-4">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto">
            <Trophy className="w-10 h-10 text-success" />
          </div>
          <h2 className="font-display text-xl font-bold">Tudo em dia!</h2>
          <p className="text-muted-foreground">
            Você não tem frases para revisar no momento.
          </p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Voltar ao Início
          </Button>
        </div>
      </Layout>
    );
  }

  // Complete state
  if (isComplete) {
    const total = results.correct + results.hard + results.wrong;
    const accuracy = Math.round((results.correct / total) * 100);

    return (
      <Layout showNav={false}>
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="text-center space-y-6 animate-scale-in max-w-sm">
            <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <Trophy className="w-12 h-12 text-success" />
            </div>
            
            <div className="space-y-2">
              <h1 className="font-display text-2xl font-bold">Revisão Completa!</h1>
              <p className="text-muted-foreground">
                Você revisou {total} frases
              </p>
            </div>

            <div className="card-elevated p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-xl bg-success/10">
                  <p className="text-2xl font-bold text-success">{results.correct}</p>
                  <p className="text-xs text-muted-foreground">Acertos</p>
                </div>
                <div className="p-3 rounded-xl bg-warning/10">
                  <p className="text-2xl font-bold text-warning">{results.hard}</p>
                  <p className="text-xs text-muted-foreground">Difíceis</p>
                </div>
                <div className="p-3 rounded-xl bg-destructive/10">
                  <p className="text-2xl font-bold text-destructive">{results.wrong}</p>
                  <p className="text-xs text-muted-foreground">Erros</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Precisão</span>
                  <span className="font-semibold">{accuracy}%</span>
                </div>
                <Progress value={accuracy} className="h-2" />
              </div>
            </div>

            <Button 
              onClick={() => navigate('/')}
              className="btn-primary-gradient px-8"
            >
              Voltar ao Início
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showNav={false}>
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background border-b border-border px-4 py-3">
          <div className="flex items-center gap-4 max-w-lg mx-auto">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
            
            <div className="flex-1">
              <Progress value={progress} className="h-2" />
            </div>
            
            <span className="text-sm text-muted-foreground shrink-0">
              {currentIndex + 1}/{reviewQueue.length}
            </span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <ReviewCard 
              key={currentPhrase.id}
              phrase={currentPhrase} 
              onResult={handleResult}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default ReviewPage;
